import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQuiz, submitQuizAnswer, completeQuiz } from '../../redux/features/studentNotebookSlice';
import { Arrowclockwise } from '../svgs/Svgs';

const Quiz = ({ courseId, quizId, token, studentId }) => {
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((s) => s.studentNotebook);
  
  
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);

  useEffect(() => {
    if (quizId && courseId && token) {
      loadQuiz();
    }
  }, [quizId, courseId, token]);

  // Removed problematic useEffect that was causing infinite loop

  // Handle question navigation and state updates
  useEffect(() => {
    if (quiz && quiz.questions && quiz.questions[currentQuestionIndex]) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      
      // Prefer locally tracked submittedAnswers to reflect answers made this session
      const submittedIndex = submittedAnswers[currentQuestionIndex];
      if (submittedIndex !== undefined) {
        const selected = currentQuestion.options[submittedIndex];
        setSelectedOption(selected);
        setAnswered(true);
        return;
      }
      
      // Fallback to server-provided flags
      // userAnswer is 0-based index from backend (0, 1, 2, 3) which maps to options array directly
      const isAnswered = currentQuestion.isAnswered || currentQuestion.is_answered;
      const userAnswerIndex = currentQuestion.userAnswer !== undefined ? currentQuestion.userAnswer : (currentQuestion.user_answer !== undefined ? currentQuestion.user_answer : null);
      
      if (isAnswered && userAnswerIndex !== null && userAnswerIndex !== undefined) {
        // userAnswerIndex is 0-based (0, 1, 2, 3) which directly maps to options array index
        const selected = currentQuestion.options[userAnswerIndex];
        if (selected) {
          setSelectedOption(selected);
          setAnswered(true);
        } else {
          console.warn(`Option at index ${userAnswerIndex} not found for question ${currentQuestion.id}`);
          setSelectedOption(null);
          setAnswered(false);
        }
      } else {
        setSelectedOption(null);
        setAnswered(false);
      }
    }
  }, [currentQuestionIndex, quiz, submittedAnswers]);

  const loadQuiz = async () => {
    try {
      console.log('Loading quiz with:', { courseId, quizId, studentId });
      const response = await dispatch(fetchQuiz({ token, courseId, quizId, studentId })).unwrap();
      console.log('Quiz API response:', response);
      
      // The API returns data in response.data.quiz and response.data.questions
      const quizData = response.data.quiz;
      const questionsData = response.data.questions;
      console.log('Quiz data:', quizData);
      console.log('Questions data:', questionsData);
      
      // Transform the data to match original QuizML format
      // IMPORTANT: Backend uses 0-based indexing (0, 1, 2, 3) for correctAnswer and userAnswer
      // Frontend maps options to A(0), B(1), C(2), D(3) to maintain consistency
      const transformedQuiz = {
        ...quizData,
        questions: questionsData.map(q => {
          const parsedOptions = JSON.parse(q.options); // ["Google Meet", "Microsoft Teams", "Zoom", "Skype"]
          const correctAnswerIndex = q.correctAnswer !== undefined ? q.correctAnswer : (q.correct_answer !== undefined ? q.correct_answer : null);
          const userAnswerIndex = q.userAnswer !== undefined ? q.userAnswer : (q.user_answer !== undefined ? q.user_answer : null);
          
          return {
            id: q.id,
            question: q.questionText || q.question_text,
            options: parsedOptions.map((option, index) => ({
              id: String.fromCharCode(65 + index), // A=0, B=1, C=2, D=3
              text: option,
              originalIndex: index, // Store original 0-based index for reference
              isCorrect: index === correctAnswerIndex // Compare using 0-based index
            })),
            correctAnswer: correctAnswerIndex, // Store as 0-based index (0, 1, 2, 3)
            correctAnswerReason: q.correctAnswerReason || q.correct_answer_reason,
            incorrectAnswerReasons: JSON.parse(q.incorrectAnswerReasons || q.incorrect_answer_reasons || "[]"), // Array with same indexing as options
            userAnswer: userAnswerIndex, // Store as 0-based index (0, 1, 2, 3)
            isAnswered: q.isAnswered || q.is_answered,
            isCorrect: q.isCorrect || q.is_correct
          };
        })
      };
      
      console.log('Transformed quiz:', transformedQuiz);
      setQuiz(transformedQuiz);
      
      // Check if quiz is already completed
      const isQuizCompleted = quizData.isCompleted || quizData.is_completed || false;
      const allQuestionsAnswered = questionsData.every(q => q.isAnswered || q.is_answered);
      
      // Multiple checks for completion status
      const finalIsCompleted = isQuizCompleted || allQuestionsAnswered || (questionsData.length > 0 && questionsData.every(q => (q.userAnswer || q.user_answer) !== null && (q.userAnswer || q.user_answer) !== undefined));
      
      setIsCompleted(finalIsCompleted);
      // Show results only if quiz is completed and not reviewing
      if (finalIsCompleted && !isReviewing) {
        setShowResults(true);
      }
      
      // If quiz is completed, set the score
      if (finalIsCompleted) {
        const correctAnswers = questionsData.filter(q => q.isCorrect || q.is_correct).length;
        const totalQuestions = questionsData.length;
        const percentage = Math.round((correctAnswers / totalQuestions) * 100);
        setScore(correctAnswers);
        
        // Set all questions as answered for display
        const existingAnswers = {};
        questionsData.forEach((question, index) => {
          const userAnswer = question.userAnswer || question.user_answer;
          if (userAnswer !== null && userAnswer !== undefined) {
            existingAnswers[index] = userAnswer;
            setSubmittedAnswers(prev => ({ ...prev, [index]: userAnswer }));
          }
        });
        setUserAnswers(existingAnswers);
      } else {
        // Initialize user answers from existing submissions for incomplete quiz
      if (questionsData) {
        const existingAnswers = {};
        questionsData.forEach((question, index) => {
          const userAnswer = question.userAnswer || question.user_answer;
          if (userAnswer !== null && userAnswer !== undefined) {
            existingAnswers[index] = userAnswer;
            setSubmittedAnswers(prev => ({ ...prev, [index]: userAnswer }));
          }
        });
        setUserAnswers(existingAnswers);
        }
      }
    } catch (err) {
      console.error('Failed to load quiz:', err);
    }
  };

  const handleOptionClick = async (option) => {
    const currentQuestion = quiz.questions[currentQuestionIndex];
    
    // Don't allow answering if quiz is completed or question is already answered
    if (isCompleted || answered || (currentQuestion.isAnswered || currentQuestion.is_answered)) return;
    
    setSelectedOption(option);
    setAnswered(true);
    
    // Submit answer to API
    try {
      await dispatch(submitQuizAnswer({
        token,
        courseId,
        quizId,
        questionId: currentQuestion.id,
        answerIndex: option.id.charCodeAt(0) - 65 // Convert A,B,C,D to 0,1,2,3
      })).unwrap();

      // Mark as submitted
      setSubmittedAnswers(prev => ({
        ...prev,
        [currentQuestionIndex]: option.id.charCodeAt(0) - 65
      }));

      // Update local quiz state so going back shows selection and stays disabled
      setQuiz(prevQuiz => {
        if (!prevQuiz) return prevQuiz;
        const updatedQuestions = [...prevQuiz.questions];
        const q = { ...updatedQuestions[currentQuestionIndex] };
        q.isAnswered = true;
        q.is_answered = true;
        q.userAnswer = option.id.charCodeAt(0) - 65;
        q.user_answer = option.id.charCodeAt(0) - 65;
        // Mark correctness locally so results immediately reflect without refresh
        const chosenIndex = option.id.charCodeAt(0) - 65;
        q.isCorrect = chosenIndex === q.correctAnswer;
        q.is_correct = q.isCorrect;
        updatedQuestions[currentQuestionIndex] = q;
        return { ...prevQuiz, questions: updatedQuestions };
      });
    } catch (err) {
      console.error('Failed to submit answer:', err);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      // Don't reset selectedOption and answered - let useEffect handle it
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      // Don't reset selectedOption and answered - let useEffect handle it
    }
  };

  const handleCompleteQuiz = async () => {
    // Don't allow completing an already completed quiz
    if (isCompleted) {
      setShowResults(true); // Show results instead
      return;
    }
    
    
    try {
      const response = await dispatch(completeQuiz({
        token,
        courseId,
        quizId,
        studentId
      })).unwrap();
      // Compute score locally for instant, accurate results display
      const computeLocalScore = () => {
        if (!quiz || !quiz.questions) return 0;
        return quiz.questions.reduce((acc, q, idx) => {
          const submittedIdx = submittedAnswers[idx];
          const answeredIdx = submittedIdx !== undefined ? submittedIdx : (q.userAnswer ?? q.user_answer);
          if (answeredIdx === undefined || answeredIdx === null) return acc;
          const isCorrectLocal = answeredIdx === q.correctAnswer;
          return acc + (isCorrectLocal ? 1 : 0);
        }, 0);
      };
      const localScore = computeLocalScore();
      setScore(response?.data?.score ?? response?.data ?? localScore);
      // Ensure questions carry correctness flags for result UI
      setQuiz(prev => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q, idx) => {
          const submittedIdx = submittedAnswers[idx];
          const answeredIdx = submittedIdx !== undefined ? submittedIdx : (q.userAnswer ?? q.user_answer);
          const isCorrectLocal = answeredIdx === q.correctAnswer;
          return {
            ...q,
            isAnswered: true,
            is_answered: true,
            isCorrect: isCorrectLocal,
            is_correct: isCorrectLocal,
            userAnswer: answeredIdx,
            user_answer: answeredIdx,
          };
        });
        return { ...prev, questions: updatedQuestions };
      });
      setIsCompleted(true);
      setShowResults(true);
    } catch (err) {
      console.error('Failed to complete quiz:', err);
      // If the error is "already completed", update the state
      if (err.message && err.message.includes('already been completed')) {
        setIsCompleted(true);
        setShowResults(true);
        // Reload quiz to get updated data
        loadQuiz();
      }
    }
  };

  if (isLoading && !quiz) {
    return (
      <div className="quiz-container">
        <div className="dottedLoading theme-color"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container">
        <div className="text-center py-4">
          <i className="bi bi-exclamation-triangle text-danger"></i>
          <p className="text-danger">Error loading quiz: {error}</p>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="quiz-container">
        <div className="text-center py-4">
          <i className="bi bi-question-circle text-muted"></i>
          <p className="text-muted">No quiz data available</p>
          <p className="text-muted small">Debug: quizId={quizId}, courseId={courseId}, token={token ? 'present' : 'missing'}</p>
        </div>
      </div>
    );
  }

  if (showResults && isCompleted) {
    const totalQuestions = quiz.questions.length;
    // Robust correct count: use local submitted answers or question flags
    const correctAnswers = quiz.questions.reduce((acc, q, idx) => {
      const submittedIdx = submittedAnswers[idx];
      const answeredIdx = submittedIdx !== undefined ? submittedIdx : (q.userAnswer ?? q.user_answer);
      const isCorrectLocal = answeredIdx === q.correctAnswer || q.isCorrect === true || q.is_correct === true;
      return acc + (isCorrectLocal ? 1 : 0);
    }, 0);
    const finalScore = score ?? quiz.score ?? correctAnswers;
    const percentage = quiz.percentage || Math.round((finalScore / totalQuestions) * 100);
    const wrongAnswers = totalQuestions - correctAnswers;
    const accuracy = Math.round((correctAnswers / totalQuestions) * 100);
    
    // Calculate performance level
    const getPerformanceLevel = (percentage) => {
      if (percentage >= 90) return { level: "Excellent", color: "success", icon: "🏆" };
      if (percentage >= 80) return { level: "Very Good", color: "primary", icon: "⭐" };
      if (percentage >= 70) return { level: "Good", color: "info", icon: "👍" };
      if (percentage >= 60) return { level: "Fair", color: "warning", icon: "📚" };
      return { level: "Needs Improvement", color: "danger", icon: "💪" };
    };
    
    const performance = getPerformanceLevel(percentage);
    
    return (
      <div className="quiz-container">
      <style>
        {`
          
          .score-circle {
            animation: pulse 2s infinite;
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          .stat-icon {
            transition: transform 0.3s ease;
          }
          
          .stat-icon:hover {
            transform: scale(1.1);
          }
          
          .quiz-results .card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 15px;
          }
          
          .quiz-results .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          }
          
          .progress {
            background-color: #e9ecef;
            border-radius: 10px;
            overflow: hidden;
          }
          
          .progress-bar {
            transition: width 1s ease-in-out;
            border-radius: 10px;
          }
          
          .result-icon {
            animation: bounce 1s ease-in-out;
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
        `}
      </style>
      <div className="quiz-results">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="result-icon">
              <span style={{ fontSize: '4rem' }}>{performance.icon}</span>
            </div>
            <h2 className={`text-${performance.color} mb-8`}>Quiz Completed!</h2>
            <p className="text-muted">Great job on finishing the quiz!</p>
          </div>



          {/* Statistics Grid */}
          <div className="row">
            <div className="col-md-4 mb-16">
              <div className="card h-100 border-success">
                <div className="card-body text-center">
                  <div className="stat-icon mb-2">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h5 className="text-success mb-1">{correctAnswers}</h5>
                  <p className="text-muted mb-0">Correct Answers</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-16">
              <div className="card h-100 border-danger">
                <div className="card-body text-center">
                  <div className="stat-icon mb-2">
                    <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h5 className="text-danger mb-1">{wrongAnswers}</h5>
                  <p className="text-muted mb-0">Wrong Answers</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-16">
              <div className="card h-100 border-info">
                <div className="card-body text-center">
                  <div className="stat-icon mb-2">
                    <i className="bi bi-graph-up text-info" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h5 className="text-info mb-1">{accuracy}%</h5>
                  <p className="text-muted mb-0">Accuracy</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Chart Section */}
          <div className="row">
            <div className="col-md-6 mb-16">
              <div className="card h-100">
                <div className="card-body text-center">
                  <h6 className="card-title mb-3">Score Distribution</h6>
                  <div className="donut-chart-container" style={{ position: 'relative', width: '200px', height: '200px', margin: '0 auto' }}>
                    <svg width="200" height="200" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke="#e9ecef"
                        strokeWidth="20"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke="#28a745"
                        strokeWidth="20"
                        strokeDasharray={`${(correctAnswers / totalQuestions) * 502.4} 502.4`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
                      />
                    </svg>
                    <div style={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#28a745'
                    }}>
                      {correctAnswers}/{totalQuestions}
                    </div>
                  </div>
                  <p className="text-muted small mt-2">Correct vs Total Questions</p>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-16">
              <div className="card h-100">
                <div className="card-body text-center">
                  <h6 className="card-title mb-3">Performance Level</h6>
                  <div className="performance-meter" style={{ position: 'relative', width: '200px', height: '200px', margin: '0 auto' }}>
                    <svg width="200" height="200" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke="#e9ecef"
                        strokeWidth="20"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke={`var(--bs-${performance.color})`}
                        strokeWidth="20"
                        strokeDasharray={`${(percentage / 100) * 502.4} 502.4`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
                      />
                    </svg>
                    <div style={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: `var(--bs-${performance.color})`
                    }}>
                      {percentage}%
                    </div>
                  </div>
                  <p className="text-muted small mt-2">{performance.level}</p>
                </div>
              </div>
            </div>
          </div>
       

          {/* Action Buttons */}
          <div className="text-center">
            <button 
              className="btn btn-primary d-inline-flex justify-content-center align-items-center gap-2 no-shadow py-7 px-10 min-w-auto"
              onClick={() => {
                setCurrentQuestionIndex(0);
                setShowResults(false);
                setIsReviewing(true);
                // Don't reset isCompleted - keep it true since quiz is completed
              }}
            >
              <Arrowclockwise />
              Review Quiz
            </button>
          
          </div>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestionIndex];
  const submittedIndexForCurrent = submittedAnswers[currentQuestionIndex];
  // Check if selected option is correct by comparing its 0-based index with correctAnswer (0-based index)
  const selectedIndex = selectedOption?.originalIndex !== undefined 
    ? selectedOption.originalIndex 
    : (selectedOption?.id ? selectedOption.id.charCodeAt(0) - 65 : null);
  const isCorrect = selectedIndex !== null && selectedIndex === question.correctAnswer;

  return (
    <div className="quiz-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="quiz-progress mb-0">
          Question {currentQuestionIndex + 1} of {quiz.questions.length}
        </p>
        {(question.isAnswered || question.is_answered) && (
          <span className="badge bg-success">
            ✓ Answered
          </span>
        )}
      </div>
      <h4 className="quiz-question">{question.question}</h4>

      <div className={`quiz-options ${answered || (question.isAnswered || question.is_answered) || submittedIndexForCurrent !== undefined || isCompleted ? "disabled" : ""}`}>
        {question.options.map((option) => {
          // option.originalIndex is 0-based (0, 1, 2, 3) matching backend indexing
          // submittedIndexForCurrent is also 0-based from backend
          // option.id.charCodeAt(0) - 65 converts A=0, B=1, C=2, D=3
          const optionIndex = option.originalIndex !== undefined ? option.originalIndex : (option.id.charCodeAt(0) - 65);
          const isSelected = selectedOption?.id === option.id || submittedIndexForCurrent === optionIndex || question.userAnswer === optionIndex;
          const isCorrectOption = option.isCorrect;
          const isQuestionAnswered = answered || (question.isAnswered || question.is_answered) || submittedIndexForCurrent !== undefined || isCompleted;

          let optionClass = "option px-20 py-11 mb-14";
          if (isQuestionAnswered) {
            if (isSelected && isCorrectOption) optionClass += " correct";
            else if (isSelected && !isCorrectOption) optionClass += " wrong";
            else if (isCorrectOption) optionClass += " correct";
          }

          return (
            <div
              key={option.id}
              className={optionClass}
              onClick={() => handleOptionClick(option)}
              style={{
                cursor: isQuestionAnswered ? 'not-allowed' : 'pointer',
                opacity: isQuestionAnswered && !isSelected ? 0.6 : 1
              }}
            >
              <span>
                <strong>{option.id}.</strong> {option.text}
              </span>

              {isQuestionAnswered && isSelected && (
                <p className={isCorrect ? "text-correct" : "text-wrong"}>
                  {isCorrect ? "✅ Yes Right Answer" : "❌ Wrong Answer"}
                </p>
              )}

              {/* Show explanation: 
                 - If user selected this option, show its explanation (correct or incorrect)
                 - Additionally, in review mode, always show the correct option's explanation even if not selected */}
              {isQuestionAnswered && (
                isSelected ? (
                <p className="explanation">
                  {isCorrect ? question.correctAnswerReason : (
                    question.incorrectAnswerReasons && Array.isArray(question.incorrectAnswerReasons) 
                      ? question.incorrectAnswerReasons[optionIndex] 
                      : "Explanation not available"
                  )}
                </p>
                ) : (
                  isReviewing && isCorrectOption && (
                    <p className="explanation text-correct">
                      {question.correctAnswerReason}
                    </p>
                  )
                )
              )}
            </div>
          );
        })}
      </div>

      <div className="quiz-buttons d-flex justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          {currentQuestionIndex > 0 && (!isCompleted || isReviewing) && (
          <button
            className="btn btn-soft-green no-shadow min-w-auto py-6 fw-normal"
            onClick={handleBack}
          >
            Previous
          </button>
        )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Next button for both review and attempt modes */}
          {currentQuestionIndex < quiz.questions.length - 1 && (
            ((isReviewing) || (!isReviewing && (answered || (question.isAnswered || question.is_answered)))) && (
          <button
            className="btn btn-primary no-shadow min-w-auto py-6 fw-normal"
            onClick={handleNext}
          >
            Next
              </button>
            )
          )}
        </div>
      </div>
        <div className='mt-10'>
          {isCompleted ? (
            <div className="text-center">
              {/* <span className="badge bg-success me-2">
                <i className="bi bi-check-circle me-1"></i>
                Quiz Completed
              </span> */}
              {isReviewing ? (
                <button
                  className="btn btn-outline-primary no-shadow min-w-auto py-6 fw-normal"
                  onClick={() => {
                    setShowResults(true);
                    setIsReviewing(false);
                  }}
                >
                  <i className="bi bi-graph-up me-1"></i>
                  Back to Results
                </button>
              ) : (
                <button
                  className="btn btn-outline-primary no-shadow min-w-auto py-6 fw-normal"
                  onClick={() => setShowResults(true)}
                >
                  <i className="bi bi-graph-up me-1"></i>
                  View Results
          </button>
        )}
            </div>
          ) : (
            <>
              {(() => {
                const shouldShowComplete = (answered || (question.isAnswered || question.is_answered)) && currentQuestionIndex === quiz.questions.length - 1 && !isCompleted;
                const shouldShowViewResults = (answered || (question.isAnswered || question.is_answered)) && currentQuestionIndex === quiz.questions.length - 1 && isCompleted;
                

                return (
                  <div className="d-flex justify-content-end align-items-center">
                    {shouldShowComplete && (
          <button
            className="btn btn-success no-shadow min-w-auto py-6 fw-normal"
            onClick={handleCompleteQuiz}
          >
            Complete Quiz
          </button>
        )}
                    
                    {shouldShowViewResults && (
                      <button
                        className="btn btn-primary no-shadow min-w-auto py-6 fw-normal"
                        onClick={() => setShowResults(true)}
                      >
                        <i className="bi bi-graph-up me-1"></i>
                        View Results
                      </button>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

      {/* Question Navigation Overview */}
      <div className="mt-4">
        {/* <div className="d-flex flex-wrap gap-2 justify-content-center">
          {quiz.questions.map((q, index) => (
            <button
              key={index}
              className={`btn btn-sm ${
                index === currentQuestionIndex 
                  ? 'btn-primary' 
                  : q.isAnswered 
                    ? 'btn-success' 
                    : 'btn-outline-secondary'
              }`}
              onClick={() => setCurrentQuestionIndex(index)}
              style={{ minWidth: '40px' }}
            >
              {index + 1}
              {q.isAnswered && <span className="ms-1">✓</span>}
            </button>
          ))}
        </div> */}
        <div className="text-center mt-46">
          <small className="text-muted">
            Answered: {quiz.questions.filter(q => q.isAnswered).length} / {quiz.questions.length}
          </small>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
